import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Trophy, Coins, Crown, Medal } from "lucide-react";

const PERIODS = [
  { key: "all", label: "All-Time" },
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

export default function Leaderboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("all");
  const [city, setCity] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/leaderboard", { params: { period, city } })
      .then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [period, city]);

  const rankStyle = (i) => i === 0 ? "border-jixxeo-gold neon-gold" : i === 1 ? "border-gray-300" : i === 2 ? "border-orange-500" : "border-white/10";
  const medal = (i) => i === 0 ? "text-jixxeo-gold" : i === 1 ? "text-gray-300" : i === 2 ? "text-orange-500" : "text-muted-foreground";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><Trophy className="w-6 h-6 text-jixxeo-gold" /><h2 className="font-head font-extrabold text-xl">Leaderboards</h2></div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`period-${p.key}`}
            className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap ${period === p.key ? "bg-jixxeo-green text-black" : "glass text-muted-foreground"}`}>
            {p.label}
          </button>
        ))}
      </div>

      <Input placeholder="Filter by city…" value={city} onChange={(e) => setCity(e.target.value)} data-testid="city-filter"
        className="bg-white/5 border-white/10 rounded-xl h-11" />

      {loading ? (
        <p className="text-center text-muted-foreground py-8">Loading ranks…</p>
      ) : rows.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-muted-foreground">
          <Trophy className="w-10 h-10 mx-auto mb-2 opacity-40" />No hunters ranked yet. Be the first!
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <button key={r.id} onClick={() => navigate(`/profile/${r.id}`)} data-testid={`rank-row-${i}`}
              className={`w-full glass rounded-2xl p-3 flex items-center gap-3 border ${rankStyle(i)} hover:scale-[1.01] transition-transform ${r.id === user?.id ? "ring-1 ring-jixxeo-green" : ""}`}>
              <div className="w-8 text-center font-display text-lg flex items-center justify-center">
                {i < 3 ? <Medal className={`w-6 h-6 ${medal(i)}`} /> : <span className="text-muted-foreground">{r.rank}</span>}
              </div>
              <Avatar user={r} size={44} />
              <div className="flex-1 min-w-0 text-left">
                <p className="font-bold text-sm truncate flex items-center gap-1">{r.name}{r.elite && <Crown className="w-3.5 h-3.5 text-jixxeo-purple" />}</p>
                <p className="text-xs text-muted-foreground">{r.rank} · Lvl {r.level}{r.city ? ` · ${r.city}` : ""}</p>
              </div>
              <div className="text-right">
                <p className="font-display text-jixxeo-green">{r.score}</p>
                <p className="text-[10px] uppercase text-muted-foreground">{period === "all" ? "XP" : "period XP"}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
